import {
    FieldQuicklook,
    GroundCondition,
    NoFlyReason,
    WeatherCondition,
    WeatherRecording,
} from "../../models/FlightConditions"
import { FlightlineEvent, FlightlineEventDTO } from "../../models/FlightlineEvent"
import { Sortie, SortieDto } from "../../models/Sortie"
import { MissionShippingInfoWithReceiptDto } from "../../models/missions/ShippingInfo"
import { SimpleProject } from "../../models/projects/Project"
import { SensorType } from "../../models/sensors/Sensor"
import { FlightplanType } from "./FlightPlansTypes"
import { ActionMap, Types } from "./Types"

export enum MissionActiveFlow {
    NO_FLIGHT = "no-flight",
    NO_FILE = "no-file",
    NO_ACQUISITION = "no-acquisition",
    FULL_FLIGHT = "full-flight",
}

export type OptionsType = {
    id?: number
    flightplan_name: string | ""
    label: string | ""
}

export type EmployeeType = {
    employee_id?: string
    employee_position?: string
    employee_email?: string
    employee_full_name: string
    employee_phone?: string
    employee_status?: string
}

export enum AcquisitionPlatform {
    FixedWing = "Fixed Wing",
    Helicopter = "Helicopter",
    UAS = "UAS",
    Automobile = "Automobile",
    Watercraft = "Watercraft",
}

export interface PlatformType {
    platform_id: number
    platform_name: string
    platform_type: AcquisitionPlatform
    notes?: string
}

/**
 * Used by DataReceipt, could be re-used
 */
export interface SimpleMission {
    mission_id: number
    mission_name: string
    operator_1_id: string | null
    operator_2_id: string | null
    project: SimpleProject
    platform: PlatformType | null
}

export type AirportLists = {
    airports: string[]
    project_airports: string[]
}

export interface EventSensorDetails {
    heading?: number
    ground_speed?: number
    sun_angle?: number
    images_planned?: number
    images_actual?: number
}

export enum FlightlineStatusOption {
    NORMAL = "Normal",
    IGNORED = "Ignored",
}

export const operatorLineStatusOptions: string[] = Object.values(FlightlineStatusOption)

/**
 * Weird name for an enum, but it's what the field is called in Mission Shipping Info.
 * Not actually an ID.
 */
export enum OfficeId {
    ANC = "ANC",
    CVO = "CVO",
    LEX = "LEX",
    PDX = "PDX",
}

export const officeIdOptions = [OfficeId.ANC, OfficeId.CVO, OfficeId.LEX, OfficeId.PDX]

export type MissionNameType = {
    mission_id?: number
    mission_name?: string
}

interface MissionBase {
    mission_id: number | null
    flight_num: number | null
    sensor_id_1: number | null
    sensor_id_2: number | null
    platform_id: number | null
    flight_plan_id: number | null
    project_number: string | null
    flown_date?: string
    operator_1_id: string | null
    operator_2_id: string | null
    pilot_1_id: string | null
    pilot_2_id: string | null
    base_airport: string | null
    manager_id: string | null
    flight_notes: string | null
    sensor_notes: string | null
    error_notes: string | null
    error_action: string | null
    tomorrow_notes: string | null
    flight_blog: string | null
    source_file: string | null
    vendor_name: string | null
    flight_plan: FlightplanType[]
    weather_recording_selections: WeatherRecording[]
    no_fly_selections: NoFlyReason[]
    weather_conditions: WeatherCondition[]
    ground_conditions: GroundCondition[]
    field_quicklook_selections: FieldQuicklook[]
}

/*
 *   From the Backend when a mission is uploaded
 * */
export interface MissionGetDto extends MissionBase {
    mission_name: string | null
    flightline_events: FlightlineEventDTO[]
    sorties: SortieDto[]

    mission_shipping_info?: TempMissionShippingInfo
}

export interface MissionPutDto extends MissionBase {
    mission_name: string | null
    sorties: SortieDto[]
}

export interface MissionParentPutDto {
    mission: MissionPutDto
    flightline_events: FlightlineEventDTO[]
    mission_shipping_info?: MissionShippingInfoWithReceiptDto
}

// Temporarily making a small type
// we will be changing this to no longer be returned from the mission endpoint
interface TempMissionShippingInfo {
    id: number
    // These fields will soon me moved directly to the mission
    plane_drive_name?: string
    backup_drive_name?: string
}

/*
 * Front end version
 * */
export interface MissionType extends MissionBase {
    mission_name: string
    sorties: Sortie[]

    // to allow for backup_drive_name and place_drive_name to exist without adding a new shipment, we are separating them to mission on the frontend only
    plane_drive_name?: string
    backup_drive_name?: string
}

export interface ParsedMissionType {
    serial_number_1: string
    serial_number_2: string
    flown_date: string
    flight_num: number
    channel_name: string | null // can be null or missing for file formats without multi-channel data
    source_file: string
    flightline_events: FlightlineEvent[]
    flight_notes: string
    sensor_notes: string
    /**
     * Created by the front-end
     */
    mission_name: string
    /*
     * Front end use only
     * */
    flown_end_date?: string
}

export interface AllEmployeeTypes {
    employees: EmployeeType[]
    project_employees: {
        pilots: string[]
        operators: string[]
        managers: string[]
        reviewers: string[]
    }
    nexus_employees: {
        pilots: string[]
        operators: string[]
        managers: string[]
        reviewers: string[]
    }
}

export type MissionsType = {
    warnings: string[]
    selectedMission: MissionType
    selectedMissionId: number | null
    parsedMissions: ParsedMissionType[]
    activeFlow: MissionActiveFlow | null
    isVendorFlow: boolean
    isDeleting: boolean
    employeeList: AllEmployeeTypes
    vendorNameList: string[]
    platformList: PlatformList
    airportList: AirportLists
    missionsErrorMessage?: string
    loadingFlightplanList: boolean
}

export interface PlatformList {
    platforms: PlatformType[]
    project_platforms: number[]
}

export type MissionsPayload = {
    [Types.Missions_ResetState]: undefined
    [Types.Missions_SelectParsedMission]: { selectedMission: ParsedMissionType; sensors: SensorType[] }
    [Types.Missions_SelectMissionId]: { selectedMissionId: number | null }
    [Types.Missions_SelectActiveFlow]: { activeFlow: MissionActiveFlow }
    [Types.Missions_UpdateMissionData]: { missionPart: Partial<MissionType>; sensors: SensorType[] }
    [Types.Missions_UpdateIsVendorFlow]: boolean
    [Types.Missions_DeleteMission]: undefined
    [Types.Missions_DeleteMissionSuccess]: undefined
    [Types.Missions_DeleteMissionError]: undefined
    [Types.Missions_FetchParsedMissions]: undefined
    [Types.Missions_FetchParsedMissionsSuccess]: {
        parsedMissions: ParsedMissionType[]
        warnings: string[]
    }
    [Types.Missions_FetchMission]: undefined
    [Types.Missions_FetchMissionSuccess]: { selectedMission: MissionType }
    [Types.Missions_FetchEmployeeList]: undefined
    [Types.Missions_FetchEmployeeListSuccess]: { employeeList: AllEmployeeTypes }
    [Types.Missions_FetchVendorNameList]: undefined
    [Types.Missions_FetchVendorNameListSuccess]: { vendorNameList: string[] }
    [Types.Missions_FetchPlatformList]: undefined
    [Types.Missions_FetchPlatformListSuccess]: PlatformList
    [Types.Missions_FetchAirportList]: undefined
    [Types.Missions_FetchAirportListSuccess]: AirportLists
    [Types.Missions_UpdateError]: { error: string }
    [Types.Missions_ClearError]: undefined
}

export type MissionsActions = ActionMap<MissionsPayload>[keyof ActionMap<MissionsPayload>]
